import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import type { LabelScope, StandardLabel, ContentType } from '@system-config/label-management/domain';
import { CREATE_STANDARD_LABEL, GET_STANDARD_LABELS } from '@system-config/label-management/graphql';
import { useState, useCallback } from 'react';

interface CreateStandardLabelInput {
  labelName: string;
  labelShortName?: string;
  policy: string;
  contentType?: ContentType;
  labelNumMap: number;
  scope: LabelScope;
}

interface CreateStandardLabelResponse {
  createStandardLabel: {
    label: StandardLabel;
  };
}

export function useCreateStandardLabel() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [createStandardLabelMutation, { loading }] = useMutation<CreateStandardLabelResponse>(CREATE_STANDARD_LABEL);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const createStandardLabel = async (input: CreateStandardLabelInput): Promise<boolean> => {
    try {
      track('CreateStandardLabelInitiated', {
        labelName: input.labelName,
      });

      await createStandardLabelMutation({
        variables: { input },
        refetchQueries: [{ query: GET_STANDARD_LABELS }],
        awaitRefetchQueries: true,
      });

      track('CreateStandardLabelSucceeded', {
        labelName: input.labelName,
      });

      return true;
    } catch (error) {
      track('CreateStandardLabelFailed', {
        labelName: input.labelName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    createStandardLabel,
    loading,
    errors,
    resetErrors,
  };
}

export default useCreateStandardLabel;
